import {
  Context,
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ITrackerValue } from "../../interfaces/User/ITrackerValue";
import { useVrsUserActions } from "../../actions/vrsUserActions";
import {
  useAppCompanyState,
  useAppUserState,
  useConfigState,
} from "../AppContext/AppContext";
import { useLocation } from "react-router";
import { useTrackerDebounce } from "./TrackerDebouncer/TrackerDebouncer";
import { TRACKER_DEBOUNCED_SEARCH_DELAY } from "../../constants/global";
import { useConfigActions } from "../../actions/configActions";
import { useLocalStorage } from "../LocalStorageContext/LocalStorageContext";
import { getCurrentUser } from '../../libs/authLib';
import Utils from '../../utilities/utils';
import config from '../../config';
import { useAppSiteState } from '../AppContext/AppContext';
import { vrsTrackerPages } from '../../constants/global';

export const initialTrackerValue: ITrackerValue = {
  CompanyId: "",
  PlantId: 0,
  Url: "",
  PathName: "",
  ActionText: "",
  ActiveUserVrsId: "not initialised",
};
const TrackerContext: Context<ITrackerValue> =
  createContext(initialTrackerValue);

export function TrackerProvider({ children }: { children: ReactElement }) {
  const [trackerValue, setTrackerValue] = useState(initialTrackerValue);

  const debouncedValue = useTrackerDebounce(
    trackerValue,
    TRACKER_DEBOUNCED_SEARCH_DELAY
  );

  const vrsUserActions = useVrsUserActions();
  const configActions = useConfigActions();
  const userLocalStorage = useLocalStorage();
  const configState = useConfigState();
  const { selectedCompanyId, selectedSiteIdForCompany } = useAppCompanyState();
  const { isAuthenticated, userProfileLarge } = useAppUserState();
  const location = useLocation();

  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    (async () => {
      if (!userProfileLarge && isAuthenticated && !configState.pageDataInitialization.trackerData.loaded) {
        try {
          configActions.setDataInitialization(
            "trackerData",
            (state) => ({ ...state, loaded: true })
          );

          const profiles = await vrsUserActions.getUserProfileAppSync(false);
          const userProfile = profiles.length > 0 ? profiles[0] : null;
          configActions.setPrefer24HourTimeFormat(
            userProfile?.Prefer24HourTimeFormat || false
          );

          const storageValue = userLocalStorage.getItem("vrsUserLocale");
          let vrsUserLocalStr =
            storageValue && storageValue !== "null" ? storageValue : "";

          if (userProfile && userProfile.Languages && userProfile.LanguageId) {
            const languageProfileItem = userProfile.Languages.find(
              (el) => el.LanguageId === userProfile.LanguageId
            );
            const languageTag = languageProfileItem.IetflanguageTag;
            if (languageProfileItem) {
              if (languageTag && languageTag === "en-US") {
                vrsUserLocalStr = "en";
              } else {
                vrsUserLocalStr =
                  languageProfileItem.IetflanguageTag.toLowerCase();
              }
            }
          }

          userLocalStorage.setItem("vrsUserLocale", vrsUserLocalStr);
          configActions.setVrsUserLocale(vrsUserLocalStr);
          setTrackerValue((s) => ({
            ...s,
            ActiveUserVrsId: userProfile ? userProfile.UserId : "not found",
          }));


        } catch (err) {
          setTrackerValue((s) => ({
            ...s,
            ActiveUserVrsId: "not found",
          }));
        }
      } else if (userProfileLarge) {
        setTrackerValue((s) => ({
          ...s,
          ActiveUserVrsId: userProfileLarge.UserId,
        }));
      }
    })();
  }, [vrsUserActions, userProfileLarge, isAuthenticated]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        setTrackerValue((s) => ({
          ...s,
          CompanyId:
            selectedCompanyId && selectedCompanyId !== "0"
              ? selectedCompanyId
              : null,
          PlantId:
            selectedSiteIdForCompany && selectedSiteIdForCompany !== "0"
              ? Number(selectedSiteIdForCompany)
              : 0,
          Url: window.location.search
            ? window.location.href
              .toString()
              .replace(window.location.search.toString(), "")
            : window.location.href, // remove search part
          ActionText: "Access",
          TrackerActionObject: configState.TrackerActionObject,
        }));
      }
      if (location.pathname !== currentUrl) {
        setCurrentUrl(location.pathname);
        setTrackerValue((s) => ({
          ...s,
          PathName: location.pathname,
        }));
      }
    })();
  }, [
    isAuthenticated,
    selectedCompanyId,
    selectedSiteIdForCompany,
    location.pathname,
    currentUrl,
    configState.TrackerActionObject,
  ]);

  const { selectedSiteId } = useAppSiteState();


  useEffect(() => {
    (async () => {
      const currentUser = await getCurrentUser();
      const username = currentUser.username;
      if (
        trackerValue.TrackerActionObject &&
        trackerValue.TrackerActionObject.Status != "Reset"
      ) {
        const path =
          trackerValue.TrackerActionObject && trackerValue.TrackerActionObject.PathName
            ? trackerValue.TrackerActionObject.PathName
            : trackerValue.PathName;
        const activePaths = vrsTrackerPages.filter((el) =>
          path.toLowerCase().startsWith(el.pathname.toLowerCase())
        );



        if (activePaths.length > 0) {
          // selected the longest
          const activePathObj =
            activePaths.length === 1
              ? activePaths[0]
              : activePaths.reduce((res, el) => {
                return el.pathname.length > res.pathname.length ? el : res;
              }, activePaths[0]);

          const { TrackerActionObject } = trackerValue;

          const actionPathRegExp = /\[(\w+)\]/g;
          const result = activePathObj.action.matchAll(actionPathRegExp);

          const queryArray = Array.from(result);
          let actionText = activePathObj.action;
          if (queryArray && queryArray.length > 0) {
            for (const item of queryArray) {
              const variable = item && item.length > 1 ? item[1] : null;
              const template = item && item.length > 0 ? item[0] : null;
              if (template && variable && TrackerActionObject[variable]) {
                actionText = actionText.replace(
                  template,
                  TrackerActionObject[variable]
                );
              }
            }
          }

          let attributesString: string | null = null;
          if (activePathObj.attributes) {
            const resolvedAttributes: { [key: string]: any } = {};
            for (const attrKey in activePathObj.attributes) {
              if (Object.prototype.hasOwnProperty.call(activePathObj.attributes, attrKey)) {
                const attributeTemplate = activePathObj.attributes[attrKey];
                const match = attributeTemplate.match(/\[(\w+)\]/);
                if (match && match[1] && TrackerActionObject[match[1]]) {
                  resolvedAttributes[attrKey] = TrackerActionObject[match[1]];
                }
              }
            }
            attributesString = JSON.stringify(resolvedAttributes);
          }

          if (activePathObj.companyIdRequired) {
            if (
              TrackerActionObject.companyId &&
              TrackerActionObject.companyId !== "0"
            ) {
              const trackerObj: any = {
                CompanyId: TrackerActionObject.companyId,
                PlantId:
                  TrackerActionObject.plantId &&
                    TrackerActionObject.plantId !== "0"
                    ? TrackerActionObject.plantId
                    : null,
                Url: trackerValue.Url,
                ActionText: actionText,
                UserId: username,
              };
              if (attributesString) {
                trackerObj.Attributes = attributesString;
              }

              console.log("SAVE TO TRACKER: ", trackerObj);
              await vrsUserActions.saveTrackerAppSync(trackerObj);

            }
          } else {
            const trackerObj: any = {
              CompanyId:
                TrackerActionObject.companyId &&
                  TrackerActionObject.companyId !== "0"
                  ? TrackerActionObject.companyId
                  : "0",
              PlantId:
                TrackerActionObject.plantId &&
                  TrackerActionObject.plantId !== "0"
                  ? TrackerActionObject.plantId
                  : null,
              Url: trackerValue.Url,
              ActionText: actionText,
              UserId: username,
            };
            if (attributesString) {
              trackerObj.Attributes = attributesString;
            }

            console.log("SAVE TO TRACKER: ", trackerObj);
            await vrsUserActions.saveTrackerAppSync(trackerObj);
          }
          if (trackerValue.TrackerActionObject) {
            // reset tracker action
            configActions.setTrackerActionObject({
              Status: "Reset",
            });
          }
        }
      }
    })();
  }, [
    trackerValue.PathName,
    trackerValue.TrackerActionObject,
    trackerValue.PlantId,
    trackerValue.Url,
    trackerValue.ActionText,
    trackerValue.CompanyId,
    vrsUserActions,
    configActions,
  ]);


  useEffect(() => {
    (async () => {
      // @ts-ignore
      if (trackerValue.ActiveUserVrsId !== "not initialised") {
        // setup the mouse flow object called _mfq using our project id
        // see https://js-api-docs.mouseflow.com/#introduction
        if (
          !Utils.isLocalhost() &&
          config.settings.MF_PROJECT != null &&
          config.settings.MF_PROJECT.toLowerCase() !== "none"
        ) {
          // @ts-ignore
          if (!window._mfq) {
            // @ts-ignore
            window._mfq = window._mfq || [];
            const mf = document.createElement("script");
            mf.type = "text/javascript";
            mf.defer = true;
            mf.src = `//cdn.mouseflow.com/projects/${config.settings.MF_PROJECT}.js`;
            document.getElementsByTagName("head")[0].appendChild(mf);
          }

          const currentUser = await getCurrentUser();

          if (currentUser) {
            // @ts-ignore
            Utils.checkVrsAbilities(currentUser).then((vrsAbilities) =>
              (window as any)._mfq.push([
                "tag",
                Utils.vrsTypeForSite(vrsAbilities, selectedSiteId),
              ])
            );
            //
            const username = currentUser.username;

            // @ts-ignore
            window._mfq.push(["identify", username]);
            // @ts-ignore
            window._mfq.push(["tag", username]);
          }

          // @ts-ignore
          window._mfq.push(["tag", new Date().toISOString().split("T")[0]]);
          console.log("MOUSEFLOW:", trackerValue.PathName);
        }
      }
    })();
  }, [trackerValue.ActiveUserVrsId, trackerValue.PathName, selectedSiteId]);

  return (
    <TrackerContext.Provider value={debouncedValue}>
      {children}
    </TrackerContext.Provider>
  );
}

export function useTracker(): ITrackerValue {
  try {
    const context = useContext(TrackerContext);
    return context;
  } catch (err) {
    throw new Error("useTracker must be used within a functional component");
  }
}
